import React, { useMemo, useState } from 'react';

import type { TransitionStatus } from 'react-transition-state';

import { LabeledAvatar } from '@change/ds-core-ui';

import type { HeroFeedItem as HeroFeedItemType } from '#src/app/pages/psf/promoteOrShare/pageContext';

import { useHeroFeedItem } from '../../hooks/useHeroFeedItem';

type Props = Readonly<{
	item: HeroFeedItemType;
	className?: string;
	animateDelay?: number;
	animateSpeed?: number;
	status?: TransitionStatus;
	role?: React.AriaRole;
}>;

export const HeroFeedItem = (({ ref: forwardedRef, item, className, animateDelay, animateSpeed, status, role }) => {
	const isDesktopMode = animateDelay !== undefined && animateSpeed !== undefined && status !== undefined;

	const hookResult = useHeroFeedItem(item, isDesktopMode);
	const { altText, displayName, displayMetaText } = hookResult.data;
	const { setContainerRef } = hookResult.actions;

	// Initial width estimate for x-small LabeledAvatar; replaced with actual offsetWidth on mount
	const DEFAULT_WIDTH = 250;
	const [elementWidth, setElementWidth] = useState<number>(DEFAULT_WIDTH);
	const ITEM_SPACING = 8;

	const handleRef = (el: HTMLSpanElement | null) => {
		setContainerRef(el);

		if (typeof forwardedRef === 'function') {
			forwardedRef(el);
		} else if (forwardedRef && 'current' in forwardedRef) {
			const refObject = forwardedRef;
			refObject.current = el;
		}
		if (el && elementWidth === DEFAULT_WIDTH) {
			setElementWidth(el.offsetWidth);
		}
	};

	const desktopStyles = useMemo(() => {
		if (!isDesktopMode) return undefined;

		const isExiting = status === 'exiting';

		return {
			transition: `margin-left ${animateSpeed}ms cubic-bezier(0.4, 0.45, 0.35, 1), opacity ${
				animateDelay + animateSpeed
			}ms`,
			display: 'inline-flex',
			opacity: isExiting ? 0 : 1,
			marginLeft: isExiting ? `${-1 * (elementWidth + ITEM_SPACING)}px` : 0,
			marginRight: `${ITEM_SPACING}px`,
		};
	}, [isDesktopMode, status, animateDelay, animateSpeed, elementWidth]);

	return (
		<span ref={handleRef} style={desktopStyles} role={role}>
			<LabeledAvatar
				alt={altText}
				container="stacked"
				metatext={displayMetaText}
				name={displayName}
				size="x-small"
				src={item.userPhoto ?? undefined}
				className={className}
			/>
		</span>
	);
}) as React.FC<
	Props & {
		ref?: React.Ref<HTMLSpanElement | null>;
	}
>;
